import { useState } from 'react'
import { Button, Input, message, Segmented, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { diffPayload } from '@/api/payloadDebug'
import type { DiffLine, DiffResult, JsonDiffItem } from '@/types'

const { Text } = Typography

const MONO = { fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: 12 }

/** 文本 Diff 行类型 Tag */
const LINE_TYPE_META: Record<DiffLine['type'], { color?: string; label: string }> = {
  same: { label: ' ' },
  changed: { color: 'warning', label: '≠' },
  removed: { color: 'error', label: '-' },
  added: { color: 'success', label: '+' },
}

/** JSON Diff 差异类型中文 */
const JSON_KIND_META: Record<JsonDiffItem['kind'], { color: string; label: string }> = {
  missing: { color: 'error', label: '预期有实际无' },
  extra: { color: 'warning', label: '实际多出' },
  mismatch: { color: 'geekblue', label: '值不一致' },
  type_mismatch: { color: 'purple', label: '类型不一致' },
}

/** 文本/JSON Diff 页签：预期 vs 实际报文对比，回归核对高频动作 */
export default function DiffTab() {
  const [mode, setMode] = useState<'text' | 'json'>('text')
  const [left, setLeft] = useState('')
  const [right, setRight] = useState('')
  const [result, setResult] = useState<DiffResult | null>(null)
  const [loading, setLoading] = useState(false)

  /** 执行对比 */
  const handleDiff = async () => {
    if (!left.trim() || !right.trim()) {
      message.warning('请填写预期与实际两份报文')
      return
    }
    setLoading(true)
    try {
      const res = await diffPayload({ left, right, mode })
      setResult(res)
      message.success(res.same ? '两份报文一致' : `发现 ${res.changed} 处差异`)
    } finally {
      setLoading(false)
    }
  }

  /** 交换预期与实际 */
  const handleSwap = () => {
    setLeft(right)
    setRight(left)
    setResult(null)
  }

  const diffColumns: ColumnsType<DiffLine> = [
    { title: '#', width: 44, render: (_, __, idx) => idx + 1 },
    {
      title: `预期（左）`,
      dataIndex: 'left',
      ellipsis: true,
      render: (v: string | null, record) => (
        <span style={{ ...MONO, color: record.type === 'removed' ? 'var(--au-danger, #cf1322)' : undefined }}>
          {v ?? ''}
        </span>
      ),
    },
    { title: ' ', width: 44, align: 'center', render: (_, r) => (r.type === 'same' ? '' : <Tag color={LINE_TYPE_META[r.type].color}>{LINE_TYPE_META[r.type].label}</Tag>) },
    {
      title: '实际（右）',
      dataIndex: 'right',
      ellipsis: true,
      render: (v: string | null, record) => (
        <span style={{ ...MONO, color: record.type === 'added' ? 'var(--au-success, #389e0d)' : undefined }}>
          {v ?? ''}
        </span>
      ),
    },
  ]

  const jsonColumns: ColumnsType<JsonDiffItem> = [
    { title: '差异路径', dataIndex: 'path', ellipsis: true, render: (v: string) => <span style={MONO}>{v}</span> },
    {
      title: '类型', width: 120,
      render: (_, r) => <Tag color={JSON_KIND_META[r.kind].color}>{JSON_KIND_META[r.kind].label}</Tag>,
    },
    { title: '预期', dataIndex: 'expected', ellipsis: true, render: (v: string | null) => <span style={MONO}>{v ?? '—'}</span> },
    { title: '实际', dataIndex: 'actual', ellipsis: true, render: (v: string | null) => <span style={MONO}>{v ?? '—'}</span> },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 12, padding: '0 22px 12px' }}>
      {/* 工具栏 */}
      <Space wrap style={{ flexShrink: 0 }}>
        <Segmented
          value={mode}
          onChange={(v) => { setMode(v as 'text' | 'json'); setResult(null) }}
          options={[{ value: 'text', label: '文本 Diff' }, { value: 'json', label: 'JSON Diff' }]}
        />
        <Button size="small" onClick={handleSwap}>交换左右</Button>
        <div style={{ flex: 1 }} />
        <Button type="primary" style={{ background: 'var(--au-primary)', border: 'none' }}
          loading={loading} onClick={handleDiff}>
          对比
        </Button>
      </Space>

      {/* 预期 vs 实际输入 */}
      <div style={{ flexShrink: 0, display: 'flex', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ marginBottom: 6, display: 'block' }}>预期报文（左）</Text>
          <Input.TextArea
            placeholder="粘贴预期响应报文…"
            value={left}
            onChange={(e) => setLeft(e.target.value)}
            rows={8}
            style={MONO}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ marginBottom: 6, display: 'block' }}>实际报文（右）</Text>
          <Input.TextArea
            placeholder="粘贴实际响应报文…"
            value={right}
            onChange={(e) => setRight(e.target.value)}
            rows={8}
            style={MONO}
          />
        </div>
      </div>

      {/* 结果区 */}
      {result && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Space style={{ marginBottom: 8, flexShrink: 0 }}>
            {result.same
              ? <Tag color="success">完全一致</Tag>
              : <Tag color="error">{result.changed} 处差异</Tag>}
            <Text type="secondary" style={{ fontSize: 12 }}>
              左 {result.left_lines} 行 / 右 {result.right_lines} 行
            </Text>
          </Space>
          {mode === 'text' ? (
            <Table
              size="small"
              columns={diffColumns}
              dataSource={result.text_diff ?? []}
              rowKey={(_, idx) => String(idx)}
              pagination={{ pageSize: 50, size: 'small', showSizeChanger: false, showTotal: (t) => `共 ${t} 行` }}
              scroll={{ y: '100%' }}
              style={{ flex: 1, minHeight: 0 }}
              onRow={(r: DiffLine) => ({ style: { background: r.type === 'same' ? undefined : 'rgba(250, 173, 20, 0.08)' } })}
            />
          ) : (
            <Table
              size="small"
              columns={jsonColumns}
              dataSource={result.json_diff ?? []}
              rowKey={(r) => r.path}
              pagination={{ pageSize: 50, size: 'small', showSizeChanger: false, showTotal: (t) => `共 ${t} 处` }}
              scroll={{ y: '100%' }}
              style={{ flex: 1, minHeight: 0 }}
            />
          )}
        </div>
      )}
    </div>
  )
}
